/**
 * BoxTruckRouteTeaser
 *
 * The placeholder that stands where the drawer's live route map used to be.
 *
 * It exists because the real map was expensive in a way nobody chose: mounting it fired an OSRM route
 * request, up to three Nominatim/Photon geocodes and thirty-odd OSM raster tiles — all against free
 * public endpoints, all on every drawer open, and operators scan the board by opening and closing loads
 * in seconds. The route is now drawn only when someone asks for it, by the Google-backed planner in
 * BoxTruckLoadMapModal, which was already click-gated.
 *
 * So this component makes NO network request of any kind. The backdrop is drawn, not fetched, and it is
 * deliberately blurred: the coastline and roads are invented, and the two pins sit at fixed positions
 * with no relation to the load's geography. The blur is the honesty — a crisp fake map would read as a
 * real one, and an operator would trust a route that was never computed.
 *
 * That rules out anything sharp that could be mistaken for data. An earlier pass drew A/B pins and a
 * dashed connector over the blur — fixed coordinates, identical on every load, in the real planner's
 * colors — which is exactly the crisp fake this comment claims to avoid. Blurring them with the rest
 * only turned them into unreadable blobs, so they moved to where they are honest: beside the city names,
 * which ARE true. Green end, red end, no claim about where either sits on a map.
 */

import { Maximize2 } from 'lucide-react'
import { CanonicalLoad } from '@numeo/types-core'
import { ROUTE_PIN_DESTINATION_COLOR, ROUTE_PIN_ORIGIN_COLOR } from '@/constants/mapPins'
import { cityLabel } from '../utils/format'

/**
 * Basemap colors as CSS variables toggled by Tailwind's class-based `dark` variant, rather than read
 * from MUI's `useColorScheme`. The hook would tie this otherwise MUI-free component to a CssVarsProvider
 * it has no other use for — and make it unrenderable without one.
 */
const PALETTE_VARS = [
    '[--bt-land:#F2EFE9] dark:[--bt-land:#2B2F35]',
    '[--bt-water:#AAD3F2] dark:[--bt-water:#21506E]',
    '[--bt-green:#CDE8C4] dark:[--bt-green:#2F4B36]',
    '[--bt-road:#FFFFFF] dark:[--bt-road:#4B525B]',
    '[--bt-major:#F6D98A] dark:[--bt-major:#615941]',
].join(' ')

/** The invented basemap. Pure geometry — no projection, no data. See the file header. */
const FauxMap = () => (
    <svg viewBox="0 0 400 180" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full" aria-hidden focusable="false">
        <rect width="400" height="180" fill="var(--bt-land)" />
        <path d="M318 -10 C300 30 330 58 312 92 C296 124 330 150 320 190 L420 190 L420 -10 Z" fill="var(--bt-water)" />
        <path d="M-10 128 C30 118 58 140 96 132 C130 125 150 150 140 190 L-10 190 Z" fill="var(--bt-water)" />
        <ellipse cx="72" cy="46" rx="54" ry="30" fill="var(--bt-green)" />
        <ellipse cx="228" cy="140" rx="70" ry="34" fill="var(--bt-green)" />
        <ellipse cx="186" cy="22" rx="46" ry="22" fill="var(--bt-green)" />
        <g stroke="var(--bt-road)" strokeWidth="3" fill="none" strokeLinecap="round">
            <path d="M-10 74 C60 66 104 96 168 88 C232 80 272 106 340 96" />
            <path d="M44 -10 C52 40 30 78 46 118 C58 150 40 170 48 190" />
            <path d="M262 -10 C254 44 278 76 268 122 C262 152 274 170 268 190" />
            <path d="M-10 148 C70 140 128 158 196 150 C252 144 292 158 340 152" />
        </g>
        <g stroke="var(--bt-major)" strokeWidth="5" fill="none" strokeLinecap="round">
            <path d="M-10 108 C80 100 140 124 214 112 C284 100 326 122 410 112" />
            <path d="M150 -10 C158 48 136 96 152 148 C160 172 152 182 156 190" />
        </g>
    </svg>
)

const Pin = ({ letter, color }: { letter: string; color: string }) => (
    <span
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
        style={{ backgroundColor: color }}
        aria-hidden
    >
        {letter}
    </span>
)

/**
 * @param load      the drawer's load — only its pickup/delivery cities are read
 * @param onExpand  open the real (Google) route planner
 */
export const BoxTruckRouteTeaser = ({ load, onExpand }: { load: CanonicalLoad; onExpand: () => void }) => {
    // Same helper and same arrow as the planner's own subtitle (BoxTruckLoadMapModal.tsx:207), so the
    // teaser and the modal it opens name the trip identically.
    const from = cityLabel(load.pickupCity, load.pickupStateProv)
    const to = cityLabel(load.deliveryCity, load.deliveryStateProv)

    return (
        <div className={`group relative h-44 w-full overflow-hidden rounded-xl border border-border ${PALETTE_VARS}`}>
            {/* Nothing here is data. scale-110 pushes the blur's own soft edges outside the frame. */}
            <div className="absolute inset-0 scale-110 blur-[6px]">
                <FauxMap />
            </div>
            <div className="absolute inset-0 bg-surface/25" />

            {/* A and B ride with the cities, not with the map. They were pins dropped at fixed
                coordinates joined by a dashed curve — identical on every load, in the real planner's
                colors, over terrain — which an operator reads as this load's lane. Anchored to the
                city names they say the same thing (green end, red end) about something that is true. */}
            <div className="absolute inset-x-2 bottom-2 flex items-center gap-1.5 rounded-lg bg-surface/90 px-2.5 py-1.5 text-xs font-medium text-foreground shadow-overlay backdrop-blur-sm">
                <span className="flex min-w-0 items-center gap-1.5">
                    <Pin letter="A" color={ROUTE_PIN_ORIGIN_COLOR} />
                    <span className="truncate">{from}</span>
                </span>
                <span className="shrink-0 text-foreground-muted">→</span>
                <span className="flex min-w-0 items-center gap-1.5">
                    <Pin letter="B" color={ROUTE_PIN_DESTINATION_COLOR} />
                    <span className="truncate">{to}</span>
                </span>
            </div>

            {/* Only this opens the planner. The whole block used to be one button, so any stray tap —
                trivial on the mobile drawer — ran a geocode and a billed Directions request, and counted
                as a deliberate `map_opened`. */}
            <button
                type="button"
                onClick={onExpand}
                aria-label={`Open route map, ${from} to ${to}`}
                className="absolute right-2 top-2 inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-semibold text-foreground shadow-overlay transition-colors hover:bg-default"
            >
                <Maximize2 size={13} strokeWidth={2} /> Expand
            </button>
        </div>
    )
}
